import {isEqual, map, omit} from 'lodash';
import {Moment} from 'moment';
import {Fragment, useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Input, Select, AssignmentSelectors, DateRangePicker,
    FiltersDropdown, FiltersWrapper, FiltersWrapperChildrenProps
} from 'components';
import {UserContext} from 'context';
import {Group, SessionFilePipelineStepStatus, SessionResultStatus, Option, ClientDevice} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {UserSummary} from 'types';
import {settingsUtils} from 'utils';
import {getParticipantName} from 'utils/transform';

import {ActivityFilterType} from '../enums';
import {ActivityFilters} from '../types';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;
const SESSION_RESULT_NAMES = TRANSLATION_KEYS.COMMON.SESSION.RESULT;

type Props = {
    users: UserSummary[];
    groups: Group[];
    defaultFilters: ActivityFilters,
    filters: ActivityFilters | null,
    availableFilters: ActivityFilterType[];
    isLoading: boolean;
    onSubmit: (filters: ActivityFilters) => void;
};

export const ActivityFiltersForm: React.FC<Props> = ({
    filters, defaultFilters: defaultFiltersProps, availableFilters, users, groups, isLoading, onSubmit
}: Props) => {
    const {t} = useTranslation();
    const {settings} = useContext(UserContext);
    const canSeeFilePipeline = useMemo(() => settingsUtils.canSeeFilePipeline(settings), [settings]);

    const handleSubmit = useCallback((_filters: ActivityFilters) => {
        onSubmit({..._filters});
    }, [onSubmit]);

    const isAvailableFilter = useCallback((filterType: ActivityFilterType) =>
        availableFilters.includes(filterType), [availableFilters]);

    const areDatesEqual = useCallback((moment1: Moment, moment2: Moment) => moment1?.format('LL') === moment2?.format('LL'), []);

    const compareFilters = useCallback((filters1, filters2) => {
        const formFilters1 = omit(filters1, ['startDate', 'endDate']);
        const formFilters2 = omit(filters2, ['startDate', 'endDate']);
        return isEqual(formFilters1, formFilters2) &&
            areDatesEqual(filters1.startDate, filters2.startDate) && areDatesEqual(filters1.endDate, filters2.endDate);
    }, [areDatesEqual]);

    const filePipelineStatusOptions = useMemo(() => [{
        label: t(T_KEYS.FILE_PIPELINE_STEP_STATUS.SUCCESS),
        value: SessionFilePipelineStepStatus.Success
    }, {
        label: t(T_KEYS.FILE_PIPELINE_STEP_STATUS.IN_PROGRESS),
        value: SessionFilePipelineStepStatus.InProgress
    }, {
        label: t(T_KEYS.FILE_PIPELINE_STEP_STATUS.ERROR),
        value: SessionFilePipelineStepStatus.Error
    }, {
        label: t(T_KEYS.FILE_PIPELINE_STEP_STATUS.INITIAL),
        value: SessionFilePipelineStepStatus.Initial
    }], [t]);

    const userOptions: Option<number>[] = useMemo(() =>
        map(users, (a) => ({
            id: Number(a.eid),
            name: getParticipantName(a)
        })), [users]);

    const sessionResultOptions: Option[] = useMemo(() => [
        {id: 'no_result', name: t(T_KEYS.FORM_CONTROLS.NO_RESULT)},
        {id: SessionResultStatus.Success, name: t(SESSION_RESULT_NAMES.SUCCESS)},
        {id: SessionResultStatus.Failed, name: t(SESSION_RESULT_NAMES.FAILED)}
    ], [t]);

    const filePipelineSelectOptions: Option[] = useMemo(() =>
        map(filePipelineStatusOptions, ({value, label}) => ({
            id: value,
            name: label
        })), [filePipelineStatusOptions]);

    const clientDeviceSelectOptions: Option[] = useMemo(() => [
        {id: ClientDevice.Browser, name: t(COMMON_T_KEYS.SESSION.CLIENT_DEVICE.BROWSER)},
        {id: ClientDevice.AndroidSDK, name: t(COMMON_T_KEYS.SESSION.CLIENT_DEVICE.ANDROID_SDK)},
        {id: ClientDevice.IosSDK, name: t(COMMON_T_KEYS.SESSION.CLIENT_DEVICE.IOS_SDK)},
        {id: ClientDevice.Phone, name: t(COMMON_T_KEYS.SESSION.CLIENT_DEVICE.PHONE)},
        {id: ClientDevice.Sip, name: t(COMMON_T_KEYS.SESSION.CLIENT_DEVICE.SIP)},
    ], [t]);

    return (
        <FiltersWrapper
            defaultFilters={defaultFiltersProps}
            filters={filters}
            compareFilters={compareFilters}
            onSubmit={handleSubmit}
        >
            {({filters: formFilters, onFiltersChange, ...rest}: FiltersWrapperChildrenProps<ActivityFilters>) => (
                <div className='tw-my-4 lg:tw-my-6 tw-flex tw-items-center tw-flex-wrap'>
                    {isAvailableFilter(ActivityFilterType.StartDate) && isAvailableFilter(ActivityFilterType.EndDate) &&
                        <DateRangePicker
                            from={formFilters.startDate}
                            to={formFilters.endDate}
                            className='tw-mr-3 -sm:tw-my-2'
                            moveDayClassName='-sm:tw-hidden'
                            onChange={(startDate, endDate, options) =>
                                onFiltersChange({startDate, endDate}, {apply: options?.method === 'arrow'})}
                        />}
                    <FiltersDropdown className='tw-mr-2 -sm:tw-my-2' isLoading={isLoading} {...rest}>
                        <Fragment>
                            {isAvailableFilter(ActivityFilterType.SessionId) &&
                                <Input
                                    name='sessionId'
                                    value={formFilters.sessionId}
                                    label={t(T_KEYS.FORM_CONTROLS.NAMES.SESSION_ID)}
                                    onChange={(id) => onFiltersChange({sessionId: id})}
                                />}
                            {isAvailableFilter(ActivityFilterType.User) &&
                                <Select
                                    placeholder={t(T_KEYS.FORM_CONTROLS.NAMES.USER)}
                                    options={userOptions}
                                    noneOption
                                    valueAs='id'
                                    value={formFilters.eid}
                                    onChange={(id) => onFiltersChange({eid: id})}
                                />}
                            <AssignmentSelectors
                                groups={groups}
                                selectedGroupId={formFilters.groupId}
                                selectedSubgroupId={formFilters.subgroupId}
                                onChange={(groupId, subgroupId) => onFiltersChange({groupId, subgroupId})}
                            >
                                {({renderGroup, renderSubgroup}) => (
                                    <Fragment>
                                        {isAvailableFilter(ActivityFilterType.Group) &&
                                    renderGroup()}
                                        {isAvailableFilter(ActivityFilterType.Subgroup) &&
                                    renderSubgroup()}
                                    </Fragment>
                                )}
                            </AssignmentSelectors>
                            {isAvailableFilter(ActivityFilterType.Result) &&
                                <Select
                                    placeholder={t(T_KEYS.FORM_CONTROLS.NAMES.RESULT)}
                                    options={sessionResultOptions}
                                    noneOption
                                    valueAs='id'
                                    value={formFilters.result}
                                    onChange={(id) => onFiltersChange({result: id})}
                                />}
                            {isAvailableFilter(ActivityFilterType.ResultInformation) &&
                                <Input
                                    name='resultInformation'
                                    value={formFilters.resultInformation}
                                    label={t(T_KEYS.FORM_CONTROLS.NAMES.RESULT_INFORMATION)}
                                    onChange={(value) => onFiltersChange({resultInformation: value})}
                                />}
                            {isAvailableFilter(ActivityFilterType.UserInfoId) &&
                                <Input
                                    name='clientId'
                                    value={formFilters.userInfoId}
                                    label={t(T_KEYS.FORM_CONTROLS.NAMES.CLIENT_ID)}
                                    onChange={(id) => onFiltersChange({userInfoId: id})}
                                />}
                            {isAvailableFilter(ActivityFilterType.ClientDevice) &&
                                <Select
                                    placeholder={t(T_KEYS.FORM_CONTROLS.NAMES.CLIENT_APPLICATION)}
                                    options={clientDeviceSelectOptions}
                                    noneOption
                                    valueAs='id'
                                    value={formFilters.clientDevice}
                                    onChange={(id) => onFiltersChange({clientDevice: id})}
                                />}
                            {canSeeFilePipeline && isAvailableFilter(ActivityFilterType.FilePipelineStatus) &&
                                <Select
                                    options={filePipelineSelectOptions}
                                    placeholder={t(T_KEYS.FORM_CONTROLS.NAMES.FILE_PIPELINE_STATUS)}
                                    noneOption
                                    valueAs='id'
                                    value={formFilters.filePipelineStatus}
                                    onChange={(id) => onFiltersChange({filePipelineStatus: id})}
                                />}
                        </Fragment>
                    </FiltersDropdown>
                </div>
            )}
        </FiltersWrapper>
    );
};
